// src/SeismicMonitoring.js
import React, { useState, useEffect, useRef } from 'react';
import { ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { MapContainer, TileLayer, CircleMarker, Popup, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import houseIconUrl from './assets/house-icon.png'; // Import the house icon
const LAT = 35.248242;
const LON = -83.695590;
const RADIUS_KM = 160; // Approximately 100 miles
const BASE_URL = "https://earthquake.usgs.gov/fdsnws/event/1/query";
const houseIcon = new L.Icon({
  iconUrl: houseIconUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});
function formatTime(date, isStart = true) {
  if (isStart) {
    return date.toISOString().slice(0, 10); // YYYY-MM-DD for start
  } else {
    return date.toISOString().slice(0, 19) + 'Z'; // full for end
  }
}
function fetchEarthquakes(start, end, minMag = null, orderBy = 'time') {
  const params = new URLSearchParams({
    format: "geojson",
    starttime: start.toISOString(),
    endtime: end.toISOString(),
    latitude: LAT,
    longitude: LON,
    maxradiuskm: RADIUS_KM,
    orderby: orderBy,
    limit: "20000",
    eventtype: "earthquake"
  });

  if (minMag !== null && minMag > 0) {
    params.append("minmagnitude", minMag);
  }

  const url = `${BASE_URL}?${params.toString()}`;
  console.log("Fetching earthquakes from:", url);

  return fetch(url)
    .then(response => {
      if (!response.ok) {
        throw new Error(`Failed to fetch earthquake data: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      const features = data.features || [];
      return features.map(f => {
        const props = f.properties;
        const geom = f.geometry.coordinates;
        return {
          time: new Date(props.time),
          magnitude: props.mag,
          place: props.place,
          depth: geom[2],
          latitude: geom[1],
          longitude: geom[0]
        };
      });
    })
    .catch(error => {
      console.error("Error fetching earthquakes:", error);
      return [];
    });
}
function SeismicMonitoring({ isMobile }) {
  const [pastQuakes, setPastQuakes] = useState([]);
  const [startDate, setStartDate] = useState(new Date(new Date().getTime() - 90 * 24 * 60 * 60 * 1000)); // Last 90 days
  const [endDate, setEndDate] = useState(new Date());
  const [minMag, setMinMag] = useState(1.0);
  const [graphQuakes, setGraphQuakes] = useState([]);
  const [error, setError] = useState(null);
  const [selectedQuake, setSelectedQuake] = useState(null);
  const mapRef = useRef(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadRecentQuakes = () => {
    const today = new Date();
    const ninetyDaysAgo = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000);
    fetchEarthquakes(ninetyDaysAgo, today, 1.0, 'time')
      .then(quakes => {
        setPastQuakes(quakes);
        setError(null);
        setLastUpdated(new Date());
      })
      .catch(err => setError(err.message));
  };

  // Load recent quakes on mount
  useEffect(() => {
    loadRecentQuakes();
  }, []);

  // Update graph when filters change
  useEffect(() => {
    if (startDate > endDate) {
      setError("Start date must be before or equal to end date.");
      return;
    }
    setError(null);
    fetchEarthquakes(startDate, endDate, minMag, 'time')
      .then(quakes => {
        setGraphQuakes(quakes.sort((a, b) => a.time - b.time));
      })
      .catch(err => setError(err.message));
  }, [startDate, endDate, minMag]);

  // Fly map to selected quake
  useEffect(() => {
    if (selectedQuake && mapRef.current) {
      mapRef.current.flyTo([selectedQuake.latitude, selectedQuake.longitude], 10, { duration: 1.2 });
    }
  }, [selectedQuake]);

  const isSameQuake = (q1, q2) => {
    return Math.abs(q1.latitude - q2.latitude) < 0.001 && Math.abs(q1.longitude - q2.longitude) < 0.001;
  };

  // Calculate distance from NMR
  const getDistance = (lat, lon) => {
    const R = 6371;
    const dLat = ((lat - LAT) * Math.PI) / 180;
    const dLon = ((lon - LON) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos((LAT * Math.PI) / 180) * Math.cos((lat * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return (R * c).toFixed(1);
  };

  const pastTable = (
    <div className="seismic-table-container">
      <table className="seismic-table">
        <thead>
          <tr>
            <th>Time</th>
            <th>Mag</th>
            <th>Depth</th>
            <th>Distance</th>
            <th>Location</th>
          </tr>
        </thead>
        <tbody>
          {pastQuakes.slice(0, 15).map((quake, index) => (
            <tr 
              key={index} 
              onClick={() => setSelectedQuake(quake)} 
              className={selectedQuake && isSameQuake(selectedQuake, quake) ? 'selected' : ''}
            >
              <td>{quake.time.toLocaleDateString()} {quake.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
              <td><strong>{quake.magnitude}</strong></td>
              <td>{quake.depth} km</td>
              <td>{getDistance(quake.latitude, quake.longitude)} km</td>
              <td className="location-cell">{quake.place}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {pastQuakes.length > 15 && <p className="table-note">Showing most recent 15 of {pastQuakes.length} events</p>}
    </div>
  );

  const chartData = graphQuakes.map(q => ({ 
    time: q.time.getTime(), 
    magnitude: q.magnitude, 
    place: q.place 
  }));

  const hasData = pastQuakes.length > 0;

  return (
    <div className="seismic-page">
      <div className="seismic-header">
        <div>
          <h2>Seismic Activity Near Nantahala</h2>
          <p className="subtitle">Real-time monitoring within 100 miles of the resort</p>
        </div>
        <div className="header-actions">
          {lastUpdated && <p className="last-updated">Last updated: {lastUpdated.toLocaleString()}</p>}
          <button onClick={loadRecentQuakes} className="refresh-btn">Refresh</button>
        </div>
      </div>

      {/* Quick Stats - nicer tile layout */}
      <div className="seismic-stats">
        <div className="stat-card">
          <div className="stat-label">Events (Last 90 Days)</div>
          <div className="stat-number">{pastQuakes.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">M2.0+ Earthquakes</div>
          <div className="stat-number">{pastQuakes.filter(q => q.magnitude >= 2).length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Largest Magnitude</div>
          <div className="stat-number">
            {pastQuakes.length > 0 
              ? Math.max(...pastQuakes.map(q => q.magnitude)).toFixed(1) 
              : '—'}
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="seismic-section">
        <h3>Map View</h3>
        <MapContainer 
          ref={mapRef} 
          center={[LAT, LON]} 
          zoom={9} 
          style={{ height: isMobile ? '320px' : '480px', width: '100%', borderRadius: '12px' }}
        >
          <TileLayer
            url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
            attribution='Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)'
          />
          <CircleMarker center={[LAT, LON]} radius={8} color="#0ea5e9" fillColor="#0ea5e9" fillOpacity={0.9}>
            <Popup>Nantahala Mountain Resort</Popup>
          </CircleMarker>
          {graphQuakes.map((quake, index) => {
            // Color code by magnitude for better visual polish
            // Better contrasting colors for topo map background
            let color = '#fde047'; // bright yellow/gold for small quakes
            if (quake.magnitude >= 2.5) color = '#fb923c'; // vibrant orange
            if (quake.magnitude >= 3.5) color = '#f87171'; // strong red for larger quakes

            return (
              <CircleMarker
                key={index}
                center={[quake.latitude, quake.longitude]}
                radius={Math.max(4, quake.magnitude * 3)}
                color={selectedQuake && isSameQuake(selectedQuake, quake) ? '#0ea5e9' : '#1e293b'}
                fillColor={selectedQuake && isSameQuake(selectedQuake, quake) ? '#0ea5e9' : color}
                fillOpacity={0.85}
                stroke={true}
                weight={selectedQuake && isSameQuake(selectedQuake, quake) ? 3.5 : 2.5}
                eventHandlers={{ click: () => setSelectedQuake(quake) }}
              >
                <Popup>
                  <strong>M{quake.magnitude.toFixed(1)}</strong><br />
                  {quake.place}<br />
                  Depth: {quake.depth} km<br />
                  {quake.time.toLocaleString()}
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>

        {/* Map Legend */}
        <div className="seismic-map-legend">
          <div><span className="legend-dot yellow"></span> M &lt; 2.5</div>
          <div><span className="legend-dot orange"></span> M 2.5 – 3.4</div>
          <div><span className="legend-dot red"></span> M 3.5+</div>
          <div><span className="legend-dot blue"></span> Selected</div>
        </div>
      </div>

      {/* Recent Events */}
      <div className="seismic-section">
        <h3>Recent Earthquakes (Last 90 Days)</h3>
        {hasData ? pastTable : <p className="seismic-no-data">No earthquakes recorded in the past 90 days within 100 miles.</p>}
      </div>

      {/* Graph */}
      <div className="seismic-section">
        <h3>Magnitude Over Time</h3>
        <div className="seismic-controls">
          <div>
            <label>Start Date</label>
            <input type="date" value={startDate.toISOString().split('T')[0]} onChange={e => setStartDate(new Date(e.target.value))} />
          </div>
          <div>
            <label>End Date</label>
            <input type="date" value={endDate.toISOString().split('T')[0]} onChange={e => setEndDate(new Date(e.target.value))} />
          </div>
          <div>
            <label>Min Magnitude: {minMag}</label>
            <input type="range" min="0" max="5" step="0.1" value={minMag} onChange={e => setMinMag(parseFloat(e.target.value))} />
          </div>
        </div>

        <div className="seismic-chart-card">
          <ResponsiveContainer width="100%" height={isMobile ? 220 : 320}>
            <ScatterChart>
              <CartesianGrid stroke="#475569" strokeDasharray="2 2" />
              <XAxis 
                type="number" 
                dataKey="time" 
                domain={['dataMin', 'dataMax']} 
                tickFormatter={(t) => new Date(t).toLocaleDateString()}
                tick={{ fill: '#e2e8f0', fontSize: 11 }}
                stroke="#64748b"
              />
              <YAxis 
                dataKey="magnitude" 
                name="Magnitude" 
                tick={{ fill: '#e2e8f0', fontSize: 11 }}
                stroke="#64748b"
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1e293b', 
                  border: '1px solid #475569', 
                  color: '#f1f5f9',
                  borderRadius: '6px'
                }}
                formatter={(value, name) => name === 'time' ? new Date(value).toLocaleString() : `M${value}`}
              />
              <Scatter 
                name="Earthquakes" 
                data={chartData} 
                fill="#67e8f9" 
                stroke="#0f172a"
                strokeWidth={1.5}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Local Context - now in a proper tile/card */}
      <div className="seismic-section">
        <div className="seismic-info-card">
          <h3>Seismic Risk in Western North Carolina</h3>
          <p>
            Western North Carolina sits in the <strong>Eastern Tennessee Seismic Zone</strong>, one of the most active seismic areas east of the Rocky Mountains. 
            While major damaging earthquakes are rare, small to moderate events (typically under M3.5) do occur regularly.
          </p>
          <ul>
            <li>The largest historical quake felt in the region occurred in 1916 (estimated M5.5–5.8) near the NC–TN border.</li>
            <li>Most modern events are too small to be felt by people, but instruments record dozens per year within 100 miles.</li>
            <li>Because the region has older, harder rock, seismic waves travel farther than they do in California — meaning a moderate quake can be felt across a wide area.</li>
          </ul>
          <p className="note">
            <strong>Bottom line:</strong> While you are unlikely to experience a damaging earthquake at Nantahala, the area is not seismically inactive. 
            The data above shows the actual recent activity in your backyard.
          </p>
        </div>
      </div>

      <div className="data-source">
        Data from USGS Earthquake Catalog • Last updated: {lastUpdated ? lastUpdated.toLocaleString() : 'Loading...'}
      </div>
    </div>
  );
}
export default SeismicMonitoring;